import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/contexts/ToastContext';
import { LoadingProvider } from './components/contexts/LoadingContext';
import { AuthProvider } from './components/contexts/AuthContext';
import { NotificationProvider } from './components/contexts/NotificationContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicRoute from './components/common/PublicRoute';
import apiService from './services/apiService';
import ROUTES from './constants/routes';
import Login from './pages/Login';
import Register from './pages/Register';
import VerificarEmail from './pages/VerificarEmail';
import Home from './pages/Home';
import Equipos from './pages/Equipos';
import OrdenesTrabajo from './pages/OrdenesTrabajo';
import Categorias from './pages/Categorias';
import TiposDocumento from './pages/TiposDocumento';
import Ubicaciones from './pages/Ubicaciones';
import AsistenciaEquipos from './pages/AsistenciaEquipos';
import PlantillasMantenimiento from './pages/PlantillasMantenimiento';
import Programas from './pages/Programas';
import Clientes from './pages/Clientes';
import Cotizaciones from './pages/Cotizaciones';
import CotizacionDetalle from './pages/CotizacionDetalle';
import GestionUsuarios from './pages/GestionUsuarios';
import GestionRoles from './pages/GestionRoles';
import AsignacionPermisos from './pages/AsignacionPermisos';
import ConnectionStatus from './components/common/ConnectionStatus';
import VerificarInforme from './pages/VerificarInforme';
import MiGaleria from './pages/MiGaleria';
import MisOrdenesTrabajo from './pages/MisOrdenesTrabajo';
import DetalleOrdenTrabajo from './pages/DetalleOrdenTrabajo';
import EjecucionActividad from './pages/EjecucionActividad';
import CalendarioOT from './pages/CalendarioOT';
import Hallazgos from './pages/Hallazgos';
import MisHallazgos from './pages/MisHallazgos';
import Analytics from './pages/Analytics';

/**
 * Componente raíz de la aplicación
 *
 * Maneja enrutamiento y protección de rutas según estado de autenticación.
 *
 * @returns {JSX.Element}
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => apiService.isAuthenticated());

  useEffect(() => {
    /**
     * Verifica estado de autenticación y actualiza estado
     */
    const checkAuth = () => {
      const isAuth = apiService.isAuthenticated();
      if (!isAuth) {
        apiService.clearAuthState();
      }
      setIsAuthenticated(isAuth);
    };

    checkAuth();

    window.addEventListener('storage', checkAuth);
    window.addEventListener('authChange', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', checkAuth);
    };
  }, []);

  return (
    <LoadingProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <NotificationProvider>
            <div className="App">
              <ConnectionStatus />
              <Routes>
                {/* Rutas públicas */}
                <Route path={ROUTES.ROOT} element={
                  <PublicRoute isAuthenticated={isAuthenticated}><Login /></PublicRoute>
                } />
                <Route path={ROUTES.LOGIN} element={
                  <PublicRoute isAuthenticated={isAuthenticated}><Login /></PublicRoute>
                } />
                <Route path={ROUTES.REGISTER} element={
                  <PublicRoute isAuthenticated={isAuthenticated}><Register /></PublicRoute>
                } />
                <Route path={ROUTES.VERIFY_EMAIL} element={
                  <PublicRoute isAuthenticated={isAuthenticated}><VerificarEmail /></PublicRoute>
                } />

                {/* Rutas abiertas — sin autenticación */}
                <Route path="/verificar-informe/:token" element={<VerificarInforme />} />

                {/* Rutas protegidas */}
                <Route path={ROUTES.HOME} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Home /></ProtectedRoute>
                } />
                <Route path={ROUTES.EQUIPOS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Equipos /></ProtectedRoute>
                } />
                <Route path={ROUTES.ORDENES_TRABAJO} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="ordenes_trabajo.read_all"><OrdenesTrabajo /></ProtectedRoute>
                } />
                <Route path={ROUTES.CATEGORIAS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Categorias /></ProtectedRoute>
                } />
                <Route path={ROUTES.TIPOS_DOCUMENTO} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><TiposDocumento /></ProtectedRoute>
                } />
                <Route path={ROUTES.UBICACIONES} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Ubicaciones /></ProtectedRoute>
                } />
                <Route path={ROUTES.ASISTENCIA} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><AsistenciaEquipos /></ProtectedRoute>
                } />
                <Route path={ROUTES.PLANTILLAS_MANTENIMIENTO} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><PlantillasMantenimiento /></ProtectedRoute>
                } />
                <Route path={ROUTES.PROGRAMAS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Programas /></ProtectedRoute>
                } />
                <Route path="/clientes" element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Clientes /></ProtectedRoute>
                } />
                <Route path="/cotizaciones" element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><Cotizaciones /></ProtectedRoute>
                } />
                <Route path="/cotizaciones/:id" element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><CotizacionDetalle /></ProtectedRoute>
                } />

                <Route path={ROUTES.MI_GALERIA} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}><MiGaleria /></ProtectedRoute>
                } />
                <Route path={ROUTES.MIS_ORDENES_TRABAJO} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="ordenes_trabajo.read"><MisOrdenesTrabajo /></ProtectedRoute>
                } />
                <Route path={ROUTES.MIS_ORDENES_TRABAJO_DETALLE} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="ordenes_trabajo.read"><DetalleOrdenTrabajo /></ProtectedRoute>
                } />
                <Route path={ROUTES.MIS_ORDENES_TRABAJO_ACTIVIDAD} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="ordenes_trabajo.read"><EjecucionActividad /></ProtectedRoute>
                } />
                <Route path={ROUTES.CALENDARIO_OT} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="ordenes_trabajo.read_all"><CalendarioOT /></ProtectedRoute>
                } />
                <Route path={ROUTES.HALLAZGOS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="hallazgos.read_all"><Hallazgos /></ProtectedRoute>
                } />
                <Route path={ROUTES.MIS_HALLAZGOS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="hallazgos.read">
                    <MisHallazgos />
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.ANALYTICS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} permission="reportes.analytics">
                    <Analytics />
                  </ProtectedRoute>
                } />

                {/* Rutas de administración (solo ADMIN) */}
                <Route path={ROUTES.GESTION_USUARIOS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} requireAdmin><GestionUsuarios /></ProtectedRoute>
                } />
                <Route path={ROUTES.GESTION_ROLES} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} requireAdmin><GestionRoles /></ProtectedRoute>
                } />
                <Route path={ROUTES.ASIGNACION_PERMISOS} element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} requireAdmin><AsignacionPermisos /></ProtectedRoute>
                } />

                {/* Fallback */}
                <Route path="*" element={<Navigate to={ROUTES.ROOT} replace />} />
              </Routes>
            </div>
            </NotificationProvider>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </LoadingProvider>
  );
}

export default App;
